// bu-switcher.js — sidebar BU picker that scopes dashboard reads
// to a single Business Unit.
//
// Phase 6.1 of the v4 multi-team daemon arc. Sits below the team
// switcher in the sidebar. When a non-default BU is selected, the
// read fetchers append `bu_id=<selected>` to their URLs via the
// `buFilterParam()` helper this module exports, which causes the
// Phase 5 BU-aware reads to narrow.
//
// Visibility rules:
//   - Solo mode (no cloud) → hidden.
//   - Tenants where /v1/teams/bus returns 404 (migration 034 not
//     applied) → hidden.
//   - Tenants with only the default "All" BU → hidden (nothing to
//     switch between).
//   - Otherwise → visible, dropdown lists "All workspaces" + each custom
//     BU.
//
// Selection persistence: per-tenant key in localStorage. A user
// switching tenants picks up that tenant's last selection (if any)
// or falls back to "All".

import { $ } from "./dom.js";
import { escapeHTML } from "./util.js";
import { api } from "./api.js";
import { teamState } from "./page-state.js";
import { state } from "./state.js";

// Module state: the BU list (cached 60s; the cache is consulted
// on boot AND after every team-bus.js mutation, since both call
// renderBuSwitcher), the current selection ("" = All), and the
// tenant for which both are valid.
let buListCache = null;
let cacheTenant = null;
let lastFetch = 0;
const FETCH_TTL_MS = 60_000;

// Sentinel for "all BUs" — never a real bu_id since real ones are
// `bu_<hex16>`.
const ALL_BUS = "";

// LocalStorage key shape: ac-bu-switcher.<tenant_id>. Per-tenant so
// multi-tenant users get the right last-selection on switch.
function lsKey(tenantId) {
  return `ac-bu-switcher.${tenantId || "default"}`;
}

let currentSelection = ALL_BUS;

// withBuFilter appends `bu_id=<selected>` to the URL it's given,
// using the right separator (`?` or `&`) based on whether the URL
// already has a query string. Returns the URL unchanged when no BU
// is selected ("All workspaces").
//
// Sweep finding (PR #127 #1): the previous shape exported
// `buFilterParam()` (leading `&`) + `buFilterParamPrefix()`
// (leading `?`) and asked the caller to pick. That created a
// silent footgun: a callsite without a leading `?` that called
// `buFilterParam` produced `/path&bu_id=...` — Go's URL parser
// treats it as a path segment, drops the filter, and the per-BU
// view shows all-BU data with no error. Phase 5 happened to have
// `?` on every read endpoint by coincidence; the next phase that
// doesn't would silently leak. One helper that takes the URL
// makes the question vanish.
export function withBuFilter(url) {
  if (!currentSelection) return url;
  const sep = url.includes("?") ? "&" : "?";
  return url + sep + "bu_id=" + encodeURIComponent(currentSelection);
}

// buFilterParam is kept as a back-compat alias for callers in
// PR #127's initial shape (returns the `&bu_id=...` fragment so
// it can be appended to a URL that already has `?something=`).
// New callers should prefer `withBuFilter(url)` for the
// separator-agnostic shape.
export function buFilterParam() {
  if (!currentSelection) return "";
  return "&bu_id=" + encodeURIComponent(currentSelection);
}

// activeBuId returns the bare selected bu_id or empty string.
// Mostly for tests and the panel header.
export function activeBuId() {
  return currentSelection;
}

// activeBuName returns a human label for the selected workspace: the
// BU's name from the cached list, "" when "All workspaces" is selected,
// or the bare id as a fallback before the list loads. Used by the
// per-workspace Settings header.
export function activeBuName() {
  if (!currentSelection) return "";
  const bu = buListCache && buListCache.find((b) => b.bu_id === currentSelection);
  return (bu && bu.name) || currentSelection;
}

// renderBuSwitcher fetches the tenant's BUs (cached) and paints
// the sidebar dropdown. Called on initial cloud-mode boot and
// after any team-switch or BU CRUD mutation.
export async function renderBuSwitcher() {
  const wrap = $("bu-switcher-wrap");
  const currentEl = $("bu-switcher-current");
  const menu = $("bu-switcher-menu");
  if (!wrap || !currentEl || !menu) return;

  const tenantId = (teamState.info && teamState.info.team_id) || "";
  if (!tenantId) {
    // Solo mode or pre-link state.
    wrap.style.display = "none";
    return;
  }

  // Restore the per-tenant selection from localStorage. Done on
  // first call AND on tenant change so multi-tenant users always
  // see the right last-pick.
  if (cacheTenant !== tenantId) {
    try {
      currentSelection = localStorage.getItem(lsKey(tenantId)) || ALL_BUS;
    } catch {
      currentSelection = ALL_BUS;
    }
  }

  const cacheValid =
    buListCache &&
    cacheTenant === tenantId &&
    Date.now() - lastFetch < FETCH_TTL_MS;
  if (!cacheValid) {
    try {
      const resp = await api("/v1/teams/bus");
      buListCache = Array.isArray(resp?.bus) ? resp.bus : [];
      cacheTenant = tenantId;
      lastFetch = Date.now();
    } catch {
      // 404 (no migration) / 403 (non-member) → hide. Other
      // errors also fall through to hide because the user has
      // no way to recover from the sidebar.
      wrap.style.display = "none";
      buListCache = [];
      cacheTenant = tenantId;
      return;
    }
  }

  // Hide if the tenant only has the default BU — nothing to
  // switch between.
  const customBus = buListCache.filter((b) => !b.is_default);
  if (customBus.length === 0) {
    wrap.style.display = "none";
    // If the user previously had a selection but now there's only
    // the default, reset to ALL_BUS so stale ?bu_id= filters don't
    // produce empty pages.
    if (currentSelection) {
      currentSelection = ALL_BUS;
      try { localStorage.removeItem(lsKey(tenantId)); } catch {}
    }
    return;
  }
  wrap.style.display = "";

  // Resolve display name for the current selection.
  const selectedBu = buListCache.find((b) => b.bu_id === currentSelection);
  if (!selectedBu && currentSelection !== ALL_BUS) {
    // Selection points at a BU that no longer exists (deleted).
    // Reset to ALL_BUS so the read endpoints don't return empty.
    currentSelection = ALL_BUS;
    try { localStorage.removeItem(lsKey(tenantId)); } catch {}
  }
  // Sentinel collision guard (PR #133 cross-PR sweep finding #3):
  // a user could create a workspace literally named "All
  // workspaces" (the 50-char limit doesn't reject it). Without a
  // disambiguator the dropdown header looks identical for "viewing
  // all" vs "viewing the workspace named All workspaces." Render
  // the sentinel italicized — a real workspace name renders
  // upright. The menu also gets italic styling on the sentinel
  // row via renderMenuItem's isAll branch.
  if (selectedBu) {
    currentEl.textContent = selectedBu.name;
    currentEl.style.fontStyle = "normal";
  } else {
    currentEl.textContent = "All workspaces";
    currentEl.style.fontStyle = "italic";
  }

  // Render the menu items. "All workspaces" first, then default-flagged,
  // then alphabetical. The defaults come right after All since
  // they're the closest semantic to "everything."
  const sorted = [...buListCache].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return a.name.localeCompare(b.name);
  });
  const items = [
    renderMenuItem(ALL_BUS, "All workspaces", currentSelection === ALL_BUS, false),
    ...sorted.map((b) =>
      renderMenuItem(b.bu_id, b.name, currentSelection === b.bu_id, b.is_default),
    ),
  ];
  menu.innerHTML = items.join("");

  // Click handlers on menu items
  menu.querySelectorAll(".bu-switcher-item").forEach((item) => {
    item.addEventListener("click", (ev) => {
      const t = ev.currentTarget;
      const next = t.getAttribute("data-bu-id") || ALL_BUS;
      selectBu(next, tenantId);
      menu.style.display = "none";
    });
  });
}

function renderMenuItem(buId, name, isCurrent, isDefault) {
  const checkmark = isCurrent
    ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`
    : `<span style="width: 14px;"></span>`;
  const defaultBadge = isDefault
    ? `<span style="font-size: 9px; padding: 1px 6px; border-radius: 999px; background: var(--accent-bg); color: var(--accent); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">Default</span>`
    : "";
  // ALL_BUS row gets a subtle visual differentiator since it's not
  // a real BU — italic name + secondary color. A real workspace
  // that happens to share the name "All workspaces" stays upright
  // in its own row, so the user can disambiguate.
  const isAll = !buId;
  const nameColor = isAll
    ? "var(--text-secondary)"
    : "var(--text)";
  const nameStyle = isAll ? "font-style: italic;" : "";
  return `
    <div class="bu-switcher-item" data-bu-id="${escapeHTML(buId)}" style="display: flex; align-items: center; gap: 8px; padding: 8px 12px; cursor: pointer; font-size: 13px; color: ${nameColor};">
      ${checkmark}
      <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; ${nameStyle}">${escapeHTML(name)}</span>
      ${defaultBadge}
    </div>`;
}

function selectBu(buId, tenantId) {
  if (buId === currentSelection) return;
  currentSelection = buId;
  try {
    if (buId) {
      localStorage.setItem(lsKey(tenantId), buId);
    } else {
      localStorage.removeItem(lsKey(tenantId));
    }
  } catch {
    // Quota / private mode / cookies disabled — selection survives
    // for the page lifetime but resets on reload. Acceptable.
  }
  // Re-render the switcher so the checkmark moves + the header
  // label updates.
  renderBuSwitcher();
  // Dispatch a custom event so any page module currently mounted
  // can re-fetch its data with the new bu_id. Re-using
  // `dashboard:tick` would also work, but `bu:changed` is more
  // specific and lets future listeners distinguish a tick from a
  // BU swap.
  // bu:changed is the BU-swap signal that bu-switcher.js's own
  // wireBuSwitcher listener consumes to reset per-page pagination
  // state. Future listeners can use it for the same reset semantic
  // (cursor/scroll reset on BU swap, distinct from the periodic
  // refresh that dashboard:tick provides).
  document.dispatchEvent(new CustomEvent("bu:changed", { detail: { bu_id: buId } }));
  // dashboard:tick is the periodic refresh signal that all page
  // modules subscribe to. Dispatching here triggers an immediate
  // re-fetch with the new bu_id rather than waiting up to 5s for
  // the next natural tick.
  document.dispatchEvent(new CustomEvent("dashboard:tick"));
}

// wireBuSwitcher attaches the open/close handlers on the toggle
// button + click-outside-closes behavior. Idempotent: a re-auth
// flow that re-bootstraps the dashboard would otherwise stack
// duplicate listeners (each one closes the menu, which is mostly
// harmless but wasteful). The module-scoped flag gates the wire
// step exactly once per page load.
let wired = false;
export function wireBuSwitcher() {
  if (wired) return;
  const btn = $("bu-switcher-btn");
  const menu = $("bu-switcher-menu");
  if (!btn || !menu) return;
  wired = true;
  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    const open = menu.style.display !== "none";
    menu.style.display = open ? "none" : "";
  });
  // Click outside the menu closes it. Use mousedown so a fresh
  // click inside fires its handler before we close.
  document.addEventListener("mousedown", (ev) => {
    const wrap = $("bu-switcher-wrap");
    if (!wrap) return;
    if (!wrap.contains(ev.target)) {
      menu.style.display = "none";
    }
  });
  // Reset per-page pagination state on BU swap. Without this, a
  // user on page 3 of the Events list for BU "Acme Marketing"
  // (1200 rows) switches to BU "Acme Eng" (40 rows) and sees an
  // empty page — the existing dashboard:tick subscribers refetch
  // but don't reset their cursor, so the slice they request is
  // past the new BU's row count.
  //
  // Filter text fields stay (the user explicitly typed them) but
  // page index resets to 0. This wires the bu:changed event into
  // something concrete; without a listener it was dead dispatch.
  document.addEventListener("bu:changed", () => {
    state.chatPage = 0;
    state.eventPage = 0;
    state.expandedChats = new Set();
  });
}

// Invalidate cache (e.g., after BU CRUD mutation). Called by
// team-bus.js so a newly-created BU shows up in the switcher
// without waiting the 60s TTL.
export function invalidateBuListCache() {
  buListCache = null;
  cacheTenant = null;
  lastFetch = 0;
}
